import React, {useState, useEffect} from 'react'
import {makeStyles} from '@mui/styles'
import {useSelector, useDispatch} from 'react-redux'
import Routes from '../../constants/RouteConstants'
import {useHistory, useLocation, matchPath} from 'react-router-dom'
import PageHeader from '../PageHeader'
import {Grid} from '@mui/material'
import {AntTab, AntTabs} from '../AntTabs'
import ClearableInput from '../ClearableInput'
import ReservationsTable from './ReservationsTable'
import Constant from './AmenitiesConstants'
import {AMENITY_TYPE_OPERATIONAL, AMENITY_TYPE_RECREATIONAL} from '../../constants/ModelConstants'
import EmptyStateIconAndMessage from '../EmptyStateIconAndMessage'
import {rxrPrimaryColor} from '../../assets/styles/color'
import DateRangeInput from '../DateRangeInput'
import AmenityReservationEditor from './AmenityReservationEditor'
import CompactAmenityReservationEditor from './CompactAmenityReservationEditor'
import {RXRButton} from '../RXRButton'
import {loadBuildingStaffForBuilding} from '../../actions/staffActions'
import {loadSisterBuildingData} from '../../actions/sisterBuildingDataActions'

import Loader from '../Loader'
import {loadAmenitiesForBuilding, loadAmenityReservationsForBuilding} from '../../actions/amenitiesActions'
import {PageContainer} from '../../assets/styles/layout'
import SidePanel from '../SidePanel'
import useCombinedResidentLookup from '../hooks/useCombinedResidentLookup'
import useBuildingDataRefresh from '../hooks/useBuildingDataRefresh'
import RXRIcon from '../RXRIcon'
import {selectActiveBuildingAndSisterBuildingIds} from '../../reducers/selectors'

const useStyles = makeStyles(() => ({
  cursorHand: {
    cursor: 'pointer',
  },
  dateFilterContainer: {
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',

    '& .MuiInput-underline:before': {
      display: 'block',
    },
  },
  textAuthDate: {
    width: '193px',
    borderBottom: '0px',
    textAlign: 'center',
  },

  placeSearch: props => ({
    width: props.isMobile ? '100%' : '418px',
    borderBottom: '0px',
  }),

  slidingPaneContainer: {},
  selectGuestCloseIcon: {
    textAlign: 'right',
  },
  peopleIconContainer: {
    fontSize: 80,
    lineHeight: 0,
    color: rxrPrimaryColor,
  },
  selectDeliveryCloseIcon: {
    textAlign: 'right',
  },
}))

export function reservationNeedsApproval(r) {
  return r.reservationState === Constant.STATE_REQUESTED
}

export function reservationIsUpcoming(r) {
  return [Constant.STATE_CONFIRMED, Constant.STATE_UNDERWAY, Constant.STATE_CONFIRMED_APPROVED].includes(r.reservationState)
}

export function reservationIsPast(r) {
  return [
    Constant.STATE_DECLINED,
    Constant.STATE_CANCELLED,
    Constant.STATE_COMPLETED,
    Constant.STATE_TIMEDOUT,
    Constant.STATE_COMPLETED_DELINQUENT,
    Constant.STATE_COMPLETED_PAID,
  ].includes(r.reservationState)
}

export function reservationIsUnderway(r) {
  return Constant.STATE_UNDERWAY === r.reservationState
}

function ViewAmenityReservationsPage({amenityType}) {
  const isMobile = useSelector(state => state.App.isMobile)
  const classes = useStyles({isMobile})

  const [tab, setTab] = useState(Constant.TAB_NEEDS_APPROVAL)
  const [filterTerm, setFilterTerm] = useState('')
  const [filterDateStart, setFilterDateStart] = useState(null)
  const [filterDateEnd, setFilterDateEnd] = useState(null)
  const history = useHistory()
  const currentLocation = useLocation()
  let focusedItemId = null
  let isCreateMode = false

  const createMatch =
    matchPath(currentLocation.pathname, {path: Routes.RECREATIONAL_AMENITIES_RESERVATIONS_CREATE}) ||
    matchPath(currentLocation.pathname, {path: Routes.OPERATIONAL_AMENITIES_RESERVATIONS_CREATE})
  const viewSingleMatch =
    matchPath(currentLocation.pathname, {path: Routes.RECREATIONAL_AMENITIES_RESERVATIONS_VIEW_SINGLE}) ||
    matchPath(currentLocation.pathname, {path: Routes.OPERATIONAL_AMENITIES_RESERVATIONS_VIEW_SINGLE})

  if (createMatch && createMatch.isExact) {
    isCreateMode = true
  } else if (viewSingleMatch && viewSingleMatch.isExact) {
    isCreateMode = false
    focusedItemId = viewSingleMatch.params['reservationId']
  }

  const dispatch = useDispatch()
  const amenityReservationsLookup = useSelector(state => state.Amenities.amenityReservationsLookup)
  const amenitiesLookup = useSelector(state => state.Amenities.amenitiesLookup)
  const buildingIds = useSelector(selectActiveBuildingAndSisterBuildingIds)
  const {getResident} = useCombinedResidentLookup()
  const {activeBuildingId, isLoadingData: isLoadingReservations} = useBuildingDataRefresh(() => {
    return Promise.all([loadAmenityReservationsForBuilding(dispatch, buildingIds), loadAmenitiesForBuilding(dispatch, buildingIds)])
  })

  const allReservations = Object.values(amenityReservationsLookup).filter(
    r => amenitiesLookup[r.amenityId] && !!amenitiesLookup[r.amenityId].isOnboardingOnly === (amenityType === AMENITY_TYPE_OPERATIONAL),
  )

  // USES THE VIEW_SINGLE / CREATE LOAD PATTERN
  useEffect(() => {
    if (focusedItemId) {
      const item = amenityReservationsLookup[focusedItemId]
      if (item) {
        if (reservationNeedsApproval(item)) {
          setTab(Constant.TAB_NEEDS_APPROVAL)
        } else if (reservationIsUpcoming(item)) {
          setTab(Constant.TAB_UPCOMING_RESERVATION)
        } else {
          setTab(Constant.TAB_PAST_RESERVATION)
        }
      }
    }
  }, [focusedItemId, amenityReservationsLookup, isLoadingReservations])

  // Load the staffLookup and sister building data on activeBuildingId changes
  useEffect(() => {
    // Load active building staff only (sister building staff loaded via loadSisterBuildingData)
    loadBuildingStaffForBuilding(dispatch, activeBuildingId).then()
    // Load sister building residents/units/occupancies/staff for amenities
    loadSisterBuildingData(dispatch, buildingIds.slice(1), activeBuildingId)
  }, [activeBuildingId, buildingIds])

  const formattedStartDate = filterDateStart ? filterDateStart.toISOString() : null
  const formattedEndDate = filterDateEnd ? filterDateEnd.toISOString() : null
  const formattedTerm = filterTerm.toLowerCase()

  let needsApprovalCount = 0
  let allReservationsFiltered = allReservations.filter(r => {
    // we're going to overload our filter function to also count needs approval
    // but only count non-shadow reservations (those without originId)
    if (reservationNeedsApproval(r) && !r.originId) {
      needsApprovalCount++
    }

    const resident = getResident(r.residentId)

    return (
      (!formattedStartDate || r.startAt >= formattedStartDate) &&
      (!formattedEndDate || r.startAt <= formattedEndDate) &&
      (amenitiesLookup[r.amenityId].label.toLowerCase().includes(formattedTerm) ||
        resident.displayName.toLowerCase().includes(formattedTerm) ||
        resident.occupancy.unit.number.toLowerCase().includes(formattedTerm))
    )
  })
  // now we filter a second time based on which tab we're on.
  let reservationsOnTab = allReservationsFiltered.filter(r => {
    if (r.originId) {
      // If this is a shadow reservation, don't render it on the table at all
      return false
    }

    switch (tab) {
      case Constant.TAB_NEEDS_APPROVAL:
        return reservationNeedsApproval(r)

      case Constant.TAB_UPCOMING_RESERVATION:
        return reservationIsUpcoming(r)

      case Constant.TAB_PAST_RESERVATION:
        return reservationIsPast(r)
    }
  })

  let visibleTableComponent

  if (reservationsOnTab.length === 0) {
    visibleTableComponent = (
      <EmptyStateIconAndMessage
        message={`There are currently no reservation requests. Select "Make a reservation" to create a new reservation.`}
        icon={RXRIcon.AMENITIES}
      />
    )
  } else {
    visibleTableComponent = (
      <ReservationsTable reservations={reservationsOnTab} tab={tab} filterTerm={filterTerm} amenityType={amenityType} />
    )
  }

  if (isLoadingReservations) {
    return <Loader />
  }

  let needsApprovalTabLabel = `Needs approval ${needsApprovalCount > 0 ? '(' + needsApprovalCount + ')' : ''}`
  const pageTitle =
    amenityType === AMENITY_TYPE_OPERATIONAL ? Constant.OPERATIONAL_RESERVATIONS_PAGE_TITLE : Constant.RECREATIONAL_RESERVATIONS_PAGE_TITLE

  return (
    <div style={PageContainer}>
      <PageHeader
        title={pageTitle}
        rightControlComponent={
          <RXRButton
            type={isMobile ? RXRButton.TYPE_DEFAULT : RXRButton.TYPE_LARGE}
            onClick={() => {
              if (amenityType === AMENITY_TYPE_OPERATIONAL) {
                history.push(Routes.OPERATIONAL_AMENITIES_RESERVATIONS_CREATE)
              } else {
                history.push(Routes.RECREATIONAL_AMENITIES_RESERVATIONS_CREATE)
              }
            }}
          >
            {Constant.BTN_ADD}
          </RXRButton>
        }
      >
        <Grid container spacing={3}>
          <Grid item xs={12} lg={6} md={6}>
            <div className={classes.placeSearch}>
              <ClearableInput value={filterTerm} onChange={setFilterTerm} placeholder={Constant.ENTER_SEARCH_KEYWORDS_CONSTANT} />
            </div>
          </Grid>

          <Grid item lg={6} md={6}>
            <div className={classes.dateFilterContainer}>
              <DateRangeInput
                onChange={(start, end) => {
                  setFilterDateStart(start)
                  setFilterDateEnd(end)
                }}
                startDate={filterDateStart}
                endDate={filterDateEnd}
              />
            </div>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={12}>
            <AntTabs value={tab} onChange={(e, t) => setTab(t)}>
              <AntTab label={needsApprovalTabLabel} />
              <AntTab label="Upcoming reservations" />
              <AntTab label="Previous reservations" />
            </AntTabs>
          </Grid>
        </Grid>
      </PageHeader>

      <div style={{height: '100%'}}>{visibleTableComponent}</div>

      <SidePanel
        isOpen={!!(focusedItemId || isCreateMode)}
        onClose={() =>
          amenityType === AMENITY_TYPE_OPERATIONAL
            ? history.push(Routes.OPERATIONAL_AMENITIES_RESERVATIONS)
            : history.push(Routes.RECREATIONAL_AMENITIES_RESERVATIONS)
        }
        title={focusedItemId ? 'Reservation request details' : 'Make a reservation'}
      >
        {amenityType === AMENITY_TYPE_OPERATIONAL ? (
          <CompactAmenityReservationEditor
            amenityReservationId={focusedItemId}
            onComplete={() => history.push(Routes.OPERATIONAL_AMENITIES_RESERVATIONS)}
            amenitiesFilter={a => a.isOnboardingOnly}
          />
        ) : (
          <AmenityReservationEditor
            amenityReservationId={focusedItemId}
            onComplete={() => history.push(Routes.RECREATIONAL_AMENITIES_RESERVATIONS)}
            amenitiesFilter={a => !a.isOnboardingOnly}
          />
        )}
      </SidePanel>
    </div>
  )
}

export default ViewAmenityReservationsPage
